import React from 'react';

const GENERIC_FILTER_COOKIE_NAME = 'generic_filter_state';
const GENERIC_FILTER_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;

type GenericFilterContextProps = {
  setOpenMobile: (open: boolean) => void;
  setOpen: (open: boolean) => void;
  open: boolean;
  openMobile: boolean;
  hasFilter: boolean;
  setHasFilter: (value: boolean) => void;
};

const GenericFilterContext = React.createContext<GenericFilterContextProps | null>(null);

export function useGenericFilter() {
  const context = React.useContext(GenericFilterContext);
  if (!context) {
    throw new Error('useGenericFilter must be used within a GenericFilterProvider.');
  }

  return context;
}

export function GenericFilterProvider({
  defaultOpenMobile = false,
  openMobile: openProp,
  onOpenChange: setOpenProp,
  children,
}: React.ComponentProps<'div'> & {
  defaultOpenMobile?: boolean;
  openMobile?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const [openMobile, setOpenMobile] = React.useState(false);
  const [hasFilter, setHasFilter] = React.useState(false);

  // This is the internal state of the generic filter.
  // We use openProp and setOpenProp for control from outside the component.
  const [_open, _setOpen] = React.useState(defaultOpenMobile);
  const open = openProp ?? _open;
  const setOpen = React.useCallback(
    (value: boolean | ((value: boolean) => boolean)) => {
      const openState = typeof value === 'function' ? value(open) : value;
      if (setOpenProp) {
        setOpenProp(openState);
      } else {
        _setOpen(openState);
      }

      // This sets the cookie to keep the generic filter state.
      document.cookie = `${GENERIC_FILTER_COOKIE_NAME}=${openState}; path=/; max-age=${GENERIC_FILTER_COOKIE_MAX_AGE}`;
    },
    [setOpenProp, open]
  );

  const contextValue = React.useMemo<GenericFilterContextProps>(
    () => ({
      open,
      setOpen,
      openMobile,
      setOpenMobile,
      hasFilter,
      setHasFilter,
    }),
    [open, setOpen, openMobile, setOpenMobile, hasFilter, setHasFilter]
  );

  return <GenericFilterContext.Provider value={contextValue}>{children}</GenericFilterContext.Provider>;
}
